import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import React, { useEffect, useState } from 'react';
import FormField from '@components/Common/FormField';
import './index.scss';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  /** What the action does and what it does not touch. */
  body: React.ReactNode;
  /**
   * When set, the exact text that must be typed before Confirm enables.
   * Use for anything irreversible; omit for a plain are-you-sure.
   */
  confirmText?: string;
  /** Names what `confirmText` is, e.g. "Email". */
  confirmLabel?: string;
  /** Defaults to "Delete". */
  actionLabel?: string;
  /**
   * The confirm button's colour. Defaults to `error`, because this dialog exists for destructive
   * actions. `primary` is for a consequential but constructive one (publishing a batch of guides),
   * where a red button would say "this destroys something" about an action that does not.
   */
  actionColor?: 'error' | 'primary';
  busy?: boolean;
  /**
   * Collect a free-text reason instead of a typed-back confirmation.
   *
   * The opposite of `confirmText`: any text is accepted, and it is handed to `onConfirm`. Added
   * for declining an employer, where the backend requires a reason and the employer is shown it,
   * so there is something to write rather than something to copy. Mutually exclusive with
   * `confirmText` in practice; if both are set, both must be satisfied.
   */
  reasonLabel?: string;
  /** Placeholder for the reason field. */
  reasonPlaceholder?: string;
  /**
   * Extra controls rendered under the fields, such as a checkbox or further inputs. The caller owns
   * their state; the dialog only lays them out. Added for cancelling a subscription, which asks a
   * yes/no question (close their live jobs too?) beside the reason, and for recording a refund,
   * whose amount and reference are not a single free-text reason.
   */
  children?: React.ReactNode;
  /**
   * Holds Confirm disabled for a reason the dialog cannot see, i.e. a required field among
   * `children`. Combined with the dialog's own checks, never instead of them.
   */
  confirmDisabled?: boolean;
  onCancel: () => void;
  /** Receives the typed reason when `reasonLabel` is set. */
  onConfirm: (_reason?: string) => void;
}

/**
 * The destructive-action confirmation.
 *
 * It replaces `window.confirm`, which the workspaces were using with a message
 * that read `Type "priya@acme.test" to confirm.` — a browser confirm has no
 * input, so there was nowhere to type and the guard was decorative. Here the
 * Confirm button is genuinely disabled until the text matches.
 *
 * Confirming by email, not by name: the email is unique and is the system's
 * identity key, while two people can share a name and a Google sign-up leaves
 * `fullName` blank. Callers pass whichever they have; an empty `confirmText`
 * would make an empty box match, so that case falls back to typing DELETE.
 */
const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  body,
  confirmText,
  confirmLabel = 'Confirm',
  actionLabel = 'Delete',
  actionColor = 'error',
  busy = false,
  reasonLabel,
  reasonPlaceholder,
  children,
  confirmDisabled = false,
  onCancel,
  onConfirm,
}) => {
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');

  // Never carry a previous answer into a new prompt.
  useEffect(() => {
    if (open) {
      setTyped('');
      setReason('');
    }
  }, [open]);

  const required = confirmText?.trim() ?? '';
  const matches = !confirmText || typed.trim().toLowerCase() === required.toLowerCase();
  const reasonGiven = !reasonLabel || reason.trim().length > 0;
  const canConfirm =
    matches && reasonGiven && !busy && !confirmDisabled && (!confirmText || required.length > 0);
  const confirm = () => onConfirm(reasonLabel ? reason.trim() : undefined);

  return (
    <Dialog className="cc-confirm" open={open} onClose={busy ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle className="cc-confirm__title">{title}</DialogTitle>
      <DialogContent className="cc-confirm__body">
        <div className="cc-confirm__text">{body}</div>
        {confirmText && (
          <>
            <p className="cc-confirm__prompt">
              Type <strong>{confirmText}</strong> to confirm.
            </p>
            {/* FormField, not a bare TextField. It carries the console's FieldLabel and
                inherits the one field spec in theme.ts, so this box matches every other
                input in the app rather than being the one with a floating MUI label. */}
            <FormField
              required={false}
              name="confirm"
              label={confirmLabel}
              value={typed}
              onChange={(_name, next) => setTyped(String(next))}
              textFieldProps={{
                autoFocus: true,
                onKeyDown: (e) => {
                  if (e.key === 'Enter' && canConfirm) confirm();
                },
              }}
            />
          </>
        )}
        {reasonLabel && (
          <FormField
            required={false}
            name="reason"
            label={reasonLabel}
            placeholder={reasonPlaceholder}
            value={reason}
            onChange={(_name, next) => setReason(String(next))}
            multiline
            rows={3}
            textFieldProps={{ autoFocus: true }}
          />
        )}
        {children}
      </DialogContent>
      <DialogActions className="cc-confirm__actions">
        <Button variant="text" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" color={actionColor} onClick={confirm} disabled={!canConfirm}>
          {busy ? 'Working…' : actionLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
